import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../../firebase';
import { Document, Folder } from '../../types/documents';
import FolderList from './FolderList';
import DocumentList from './DocumentList';
import UploadModal from './UploadModal';
import CreateFolderModal from './CreateFolderModal';
import AppLayout from '../layout/AppLayout';
import toast from 'react-hot-toast';
import { FolderPlus, Upload, Search, ArrowLeft } from 'lucide-react';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useSchool } from '../../contexts/SchoolContext';

export default function DocumentsPage() {
  useDocumentTitle('Documents');
  const { selectedSchool } = useSchool();
  
  const [documents, setDocuments] = useState<Document[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [currentFolder, setCurrentFolder] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    // Subscribe to folders
    const unsubscribeFolders = onSnapshot(
      query(collection(db, 'folders'), orderBy('name')),
      (snapshot) => {
        const folderData: Folder[] = [];
        snapshot.forEach((doc) => {
          folderData.push({ id: doc.id, ...doc.data() } as Folder);
        });
        setFolders(folderData);
      },
      (error) => {
        console.error('Error loading folders:', error);
        toast.error('Failed to load folders');
      }
    );

    // Subscribe to documents
    const unsubscribeDocuments = onSnapshot(
      query(collection(db, 'documents'), orderBy('name')),
      (snapshot) => {
        const documentData: Document[] = [];
        snapshot.forEach((doc) => {
          documentData.push({ id: doc.id, ...doc.data() } as Document);
        });
        setDocuments(documentData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading documents:', error);
        toast.error('Failed to load documents');
        setLoading(false);
      }
    );

    return () => {
      unsubscribeFolders();
      unsubscribeDocuments();
    };
  }, []);

  const handleUpload = async (files: FileList, folderId?: string | null) => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      for (const file of Array.from(files)) {
        // Upload file to Firebase Storage
        const storageRef = ref(storage, `documents/${user.uid}/${file.name}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);

        // Create document record in Firestore
        await addDoc(collection(db, 'documents'), {
          name: file.name,
          type: file.type,
          size: file.size,
          url,
          folderId: folderId || null,
          createdBy: user.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          accessLevel: 'view',
          tags: [],
        });
      }

      toast.success('Files uploaded successfully');
      setShowUploadModal(false);
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload files');
    }
  };

  const handleCreateFolder = async (name: string, description?: string) => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      await addDoc(collection(db, 'folders'), {
        name,
        description,
        parentId: currentFolder || null,
        schoolId: selectedSchool?.id || null,
        createdBy: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        accessLevel: 'edit',
      });

      toast.success('Folder created successfully');
      setShowCreateFolderModal(false);
    } catch (error) {
      console.error('Error creating folder:', error);
      toast.error('Failed to create folder');
    }
  };

  const filteredDocuments = documents.filter(doc => {
    // Match documents to current folder: both null/undefined mean root level
    const docFolderId = doc.folderId ?? null;
    const matches = docFolderId === currentFolder;
    return matches &&
      (searchQuery
        ? doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          doc.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
        : true);
  });

  const filteredFolders = folders.filter(folder => {
    // Match folders to current parent: both null/undefined mean root level
    const folderParentId = folder.parentId ?? null;
    const matches = folderParentId === currentFolder;
    return matches &&
      (searchQuery
        ? folder.name.toLowerCase().includes(searchQuery.toLowerCase())
        : true);
  });

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-text-secondary">Loading documents...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Back button when in a subfolder */}
          {currentFolder && (
            <button
              onClick={() => {
                const folder = folders.find(f => f.id === currentFolder);
                setCurrentFolder(folder?.parentId ?? null);
              }}
              className="flex items-center gap-2 text-primary-500 hover:text-primary-600 mb-4
                       transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to {folders.find(f => f.id === folders.find(cf => cf.id === currentFolder)?.parentId)?.name || 'root'}
            </button>
          )}

          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-semibold text-text-primary">Documents</h1>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowCreateFolderModal(true)}
                className="px-4 py-2 text-text-primary hover:bg-background rounded-lg 
                         transition-colors flex items-center gap-2"
              >
                <FolderPlus className="w-5 h-5" />
                New Folder
              </button>
              <button
                onClick={() => setShowUploadModal(true)}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                         transition-colors flex items-center gap-2"
              >
                <Upload className="w-5 h-5" />
                Upload Files
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search documents and folders..."
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-border-light bg-background 
                         focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              />
            </div>
          </div>

          {/* Content */}
          <div className="bg-surface-light rounded-xl shadow-sm">
            <FolderList
              folders={filteredFolders}
              onFolderClick={setCurrentFolder}
              onFolderCreate={() => setShowCreateFolderModal(true)}
            />
            <DocumentList
              documents={filteredDocuments}
              onUpload={() => setShowUploadModal(true)}
            />
          </div>
        </div>
      </div>

      {/* Modals */}
      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onUpload={(files) => handleUpload(files, currentFolder)}
          currentFolder={folders.find(f => f.id === currentFolder)}
        />
      )}

      {showCreateFolderModal && (
        <CreateFolderModal
          onClose={() => setShowCreateFolderModal(false)}
          onCreate={handleCreateFolder}
          parentFolder={folders.find(f => f.id === currentFolder)}
        />
      )}
    </AppLayout>
  );
}